import type { MouseEvent } from "react";

import { WarningCircleIcon } from "@phosphor-icons/react";
import { Controller, type UseFormReturn } from "react-hook-form";

import { Banner, ToggleOptions } from "@alpacahq/ui/old";

import { FixedBottomMenu } from "../FixedBottomMenu";
import { Question } from "./Question";
import type { NyseQuestionsSchema } from "./schema";

const NYSE_QUESTIONS: Record<keyof NyseQuestionsSchema, string> = {
  qA: "Do you use Market Data exclusively for your personal, non-commercial purposes?",
  qB: "Do you use Market Data for your business or any other entity?",
  qC: "Are you currently registered or qualified with any securities or commodities regulator?",
  qD: "Do you perform professional functions that typically require financial regulatory registration?",
  qE: "Do you do any work that would normally require you to be registered with a financial regulator like the SEC or CFTC?",
  qF: "Are you compensated for providing investment advice to any individual or entity?",
  qG: "Are you engaged as an asset manager?",
  qH: "Do you use or control the capital of any other individual or entity to trade?",
  qI: "Do you conduct trading in an account held in the name of a business or other entity?",
  qJ: "Are you compensated for your trading activities, or do you share trading profits with any third party?",
  qK: "Do you receive non-cash benefits (e.g., office space, equipment) in exchange for trading activities?",
};

interface EligibilityCheckPresentationProps {
  isLoading: boolean;
  form: UseFormReturn<NyseQuestionsSchema>;
  isIncomplete: boolean;
  isProfessional: boolean;
  onBackClicked: () => void;
  onContinueClicked: (formValues: NyseQuestionsSchema) => void;
  onContactUsClicked: (event: MouseEvent<HTMLButtonElement>) => void;
}

export const EligibilityCheckPresentation = ({
  isLoading,
  form,
  isProfessional,
  isIncomplete,
  onBackClicked,
  onContinueClicked,
  onContactUsClicked,
}: EligibilityCheckPresentationProps) => {
  const { control, handleSubmit } = form;

  const isNextButtonDisabled = isIncomplete;

  return (
    <form onSubmit={handleSubmit(onContinueClicked)} className="flex flex-col">
      <div className="flex flex-col gap-6">
        {Object.entries(NYSE_QUESTIONS).map(([key, q]) => {
          const name = key as keyof NyseQuestionsSchema;

          return (
            <Question key={name} q={q}>
              <Controller
                name={name}
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <ToggleOptions<boolean>
                    ariaLabel={q}
                    options={[
                      { label: "Yes", value: true },
                      { label: "No", value: false },
                    ]}
                    {...field}
                  />
                )}
              />
            </Question>
          );
        })}
      </div>
      <FixedBottomMenu
        aboveButtons={
          isProfessional && (
            <Banner
              message="At this time, Algo Trader Plus is available only to non-professional subscribers."
              variant="error"
              className="w-full border border-red-600 font-medium text-gray-800"
              icon={<WarningCircleIcon size={20} className="text-red-600" />}
            />
          )
        }
        fixedOnBottom
        back={{
          label: "Back",
          onClick: onBackClicked,
        }}
        next={{
          label: isProfessional ? "Contact Us" : "Continue",
          disabled: isProfessional ? false : isNextButtonDisabled,
          loading: isLoading,
          onClick: isProfessional
            ? onContactUsClicked
            : handleSubmit(onContinueClicked),
          type: isProfessional ? "button" : "submit",
        }}
      />
    </form>
  );
};
